import { useContext, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import AppContext from '@/common/context/app.context'

import { getFilters, getFiltersWithValue, getPayload, setSearchParamsOnInit, transformFilters } from './helpers'

const useFilters = ({
  filters = [],
  filterValues,
  fetchData,
  doInitialRequest = false,
  nameOfFiltersInContext = 'filters',
  prevCursor,
  nextCursor,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [state, setState] = useState([])
  const firstFetchingRef = useRef(false)
  const init = useRef(false)
  const { updateContext } = useContext(AppContext)

  useEffect(() => {
    if (state.length) {
      let filters = getPayload(state)
      filters.prevCursor = prevCursor
      filters.nextCursor = nextCursor
      updateContext(context => ({ ...context, [nameOfFiltersInContext]: filters }))
    }
  }, [nameOfFiltersInContext, nextCursor, prevCursor, state, updateContext])

  useEffect(() => {
    if (init.current === false && filterValues) {
      setSearchParamsOnInit(transformFilters(filters, searchParams, filterValues), setSearchParams)
      setState(transformFilters(filters, searchParams, filterValues))
      init.current = true
    }
  }, [filterValues, filters, searchParams, setSearchParams])

  useEffect(() => {
    if (doInitialRequest && !firstFetchingRef.current && filterValues) {
      firstFetchingRef.current = true
      fetchData(getPayload(transformFilters(filters, searchParams, filterValues)))
    }
  }, [doInitialRequest, fetchData, filterValues, filters, searchParams])

  const updateFilter = (name, value, refetchData = true) => {
    const newState =
      state?.map(filter => {
        if (filter.name === name) filter.value = value
        return filter
      }) || []

    setSearchParams({
      ...getFiltersWithValue(newState),
    })

    setState(newState)

    if (refetchData) {
      refetch(getPayload(newState))
    }
  }

  const refetch = payload => {
    fetchData(payload || getPayload(state))
  }

  const refetchWithoutFilters = () => {
    fetchData()
  }

  const getEmptyValueForFilterType = type => {
    const values = {
      string: '',
      number: null,
      array: [],
    }

    return values[type]
  }

  const onChangeView = view => {
    for (let filter of filters) {
      if (filter.name !== 'page') {
        const value = view.filters[filter.backendName] || view.filters[filter.name]
        if (value) {
          updateFilter(filter.name, value, false)
        } else {
          const emptyValue = filter.defaultValue ?? getEmptyValueForFilterType(filter.type)
          updateFilter(filter.name, emptyValue, false)
        }
      }
    }
    refetch()
  }

  return {
    ...getFilters(state),
    refetch,
    updateFilter,
    getFilters: () => getPayload(state),
    refetchWithoutFilters,
    onChangeView,
  }
}

export default useFilters
